import React, { useEffect, useRef } from 'react';
import { BoxSelection, BoxSelectionBoxSize, Checkbox, Text, TextButton } from '@wix/wix-ui-tpa/cssVars';
import { useConfig } from '../../../../contexts/config-context';
import { useFocusFieldEvent } from '../../../../contexts/events-context';
import { ForcedState, useForcedState } from '../../../../contexts/forced-state-context';
import { useTranslation } from '../../../../contexts/translation-context';
import { bulkSlotsSize, DataStatus } from '../../appointment-state';
import { Actions, Components } from '../../bi-types';
import { useAppointmentContext } from '../../context/appointment-context';
import { Loader } from '../../loader';
import { Notification } from '../../notification';
import { useAppointmentBi } from '../../utils/bi';
import { classes, st } from './slots.st.css';
import { formatTimeRange, getFormattedDateLabel, getSlotByKey, getSlotIndex, getSlotKey } from './utils';
export const TimeSlots = _ref => {
  let {
    isReloadingSlots,
    fieldId
  } = _ref;
  const {
    appointmentFieldProps,
    appointmentState,
    setAppointmentState
  } = useAppointmentContext();
  const selectedSlot = appointmentFieldProps == null ? void 0 : appointmentFieldProps.value;
  const {
    t
  } = useTranslation();
  const [selectedId, setSelectedId] = React.useState(() => getSlotKey(selectedSlot));
  const [shouldFocus, setShouldFocus] = React.useState(false);
  const {
    isMobile,
    isComposer,
    language
  } = useConfig();
  const {
    availabilitySlots,
    timezone = '',
    currentSlotBulkSize,
    availabilityDates
  } = appointmentState || {};
  const bi = useAppointmentBi();
  const forcedState = useForcedState();
  const boxSelectionRef = useRef(undefined);
  const visibility = isReloadingSlots || (appointmentState == null ? void 0 : appointmentState.dataStatus) === DataStatus.LOADING ? 'hidden' : 'visible';
  const firstSlotRef = useRef(null);
  useFocusFieldEvent(() => {
    var _firstSlotRef$current;
    (_firstSlotRef$current = firstSlotRef.current) == null || _firstSlotRef$current.focus();
  }, fieldId);
  useEffect(() => {
    if (shouldFocus && currentSlotBulkSize) {
      var _boxSelectionRef$curr;
      (_boxSelectionRef$curr = boxSelectionRef.current) == null || _boxSelectionRef$curr.focus(currentSlotBulkSize - 4);
    }
  }, [shouldFocus, boxSelectionRef, currentSlotBulkSize]);
  useEffect(() => {
    setSelectedId(getSlotKey(selectedSlot));
    if (availabilitySlots != null && availabilitySlots.length && currentSlotBulkSize) {
      const key = getSlotKey(selectedSlot);
      const slotIndex = getSlotIndex(key, availabilitySlots);
      if (slotIndex && slotIndex + 1 > currentSlotBulkSize) {
        setAppointmentState({
          currentSlotBulkSize: slotIndex + 1
        });
      }
    } else if (!(availabilitySlots != null && availabilitySlots.length)) {
      appointmentFieldProps == null || appointmentFieldProps.onChange == null || appointmentFieldProps.onChange(undefined);
    }
    setShouldFocus(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlot, availabilitySlots, availabilityDates]);
  const onSlotSelected = slotId => {
    if (appointmentFieldProps != null && appointmentFieldProps.onChange && availabilitySlots) {
      var _newSelectedSlot$slot, _newSelectedSlot$slot2;
      const newSelectedSlot = getSlotByKey(slotId, availabilitySlots);
      if (newSelectedSlot != null && (_newSelectedSlot$slot = newSelectedSlot.slot) != null && _newSelectedSlot$slot.endDate && newSelectedSlot != null && (_newSelectedSlot$slot2 = newSelectedSlot.slot) != null && _newSelectedSlot$slot2.startDate) {
        bi == null || bi.report({
          action: Actions.CLICK,
          component: Components.SLOT,
          startTime: new Date(newSelectedSlot.slot.startDate).valueOf(),
          endTime: new Date(newSelectedSlot.slot.endDate).valueOf()
        });
        appointmentFieldProps == null || appointmentFieldProps.onChange({
          startDate: newSelectedSlot.slot.startDate,
          endDate: newSelectedSlot.slot.endDate,
          timeZone: timezone
        });
      }
    }
  };
  const isMoreSlotsToDisplay = () => {
    var _appointmentState$ava, _appointmentState$ava2;
    const bulkSize = appointmentState.currentSlotBulkSize || bulkSlotsSize;
    return !!(appointmentState != null && (_appointmentState$ava = appointmentState.availabilitySlots) != null && _appointmentState$ava.length && bulkSize < (appointmentState == null || (_appointmentState$ava2 = appointmentState.availabilitySlots) == null ? void 0 : _appointmentState$ava2.length));
  };
  const onShowMoreClicked = () => {
    if (isMoreSlotsToDisplay()) {
      bi == null || bi.report({
        action: Actions.CLICK,
        component: Components.SHOW_MORE
      });
      const bulkSize = appointmentState.currentSlotBulkSize || bulkSlotsSize;
      setShouldFocus(true);
      setAppointmentState({
        currentSlotBulkSize: bulkSize + bulkSlotsSize
      });
    }
  };
  const renderSlots = () => {
    if (!(availabilitySlots != null && availabilitySlots.length)) {
      return /*#__PURE__*/React.createElement(Notification, {
        message: t('form.appointment.slots-not-found.text')
      });
    }
    return availabilitySlots.slice(0, appointmentState.currentSlotBulkSize).map((slot, index) => {
      var _slot$slot, _slot$slot2;
      if (!slot.slot || !slot.slot.startDate || !slot.slot.endDate) {
        return null;
      }
      const {
        startDate,
        endDate
      } = slot.slot;
      const timeRange = formatTimeRange({
        startDate,
        endDate,
        locale: language,
        timezone
      });
      const key = getSlotKey({
        startDate: slot == null || (_slot$slot = slot.slot) == null ? void 0 : _slot$slot.startDate,
        endDate: slot == null || (_slot$slot2 = slot.slot) == null ? void 0 : _slot$slot2.endDate
      });
      return /*#__PURE__*/React.createElement(BoxSelection.Option, {
        key: key,
        id: key,
        className: classes.boxSelectionOption,
        checked: selectedId === key,
        "aria-label": timeRange,
        ref: index === 0 ? firstSlotRef : undefined
      }, /*#__PURE__*/React.createElement(Text, {
        className: classes.timeRange
      }, timeRange));
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: st(classes.root, {
      isMobile,
      forcedHover: forcedState.includes(ForcedState.INPUT_FIELDS_HOVER),
      isComposer
    })
  }, isReloadingSlots && /*#__PURE__*/React.createElement("div", {
    className: classes.verticalCenterLoader
  }, /*#__PURE__*/React.createElement(Loader, null)), /*#__PURE__*/React.createElement("div", {
    style: {
      visibility
    }
  }, /*#__PURE__*/React.createElement(BoxSelection, {
    size: BoxSelectionBoxSize.small,
    className: classes.boxSelection,
    name: "hour-selection",
    label: getFormattedDateLabel({
      selectedDate: appointmentState.selectedDate,
      locale: language,
      timezone
    }),
    onChange: _ref2 => {
      let {
        id
      } = _ref2;
      return onSlotSelected(id);
    },
    ref: boxSelectionRef
  }, renderSlots()), !!isMoreSlotsToDisplay() && /*#__PURE__*/React.createElement(TextButton, {
    className: classes.textButton,
    onClick: () => onShowMoreClicked()
  }, t('form.appointment.show-more-slots.text'))));
};
TimeSlots.includeCheckboxInBundle = Checkbox;
//# sourceMappingURL=index.js.map